import type {
  CreateTenantDestinationPayload,
  TenantDestinationListParams,
  UpdateTenantDestinationPayload,
} from '@/shared/api/types/tenant-destination-types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { POI_QUERY_KEYS } from '@/entities/poi/api/poi-queries'
import { TOUR_QUERY_KEYS } from '@/entities/tour/api/tour-queries'
import {
  createTenantDestination,
  deleteTenantDestination,
  getTenantDestinationById,
  listTenantDestinations,
  updateTenantDestination,
} from '@/shared/api/services/tenant-destination-service'

export const TENANT_DESTINATION_QUERY_KEYS = {
  all: ['tenant-destinations'] as const,
  listPrefix: () => [...TENANT_DESTINATION_QUERY_KEYS.all, 'list'] as const,
  list: (params: TenantDestinationListParams, languageCode?: string) =>
    [...TENANT_DESTINATION_QUERY_KEYS.all, 'list', params, languageCode ?? 'default'] as const,

  detailPrefix: (id: string) => [...TENANT_DESTINATION_QUERY_KEYS.all, 'detail', id] as const,
  detail: (id: string, languageCode?: string) =>
    [...TENANT_DESTINATION_QUERY_KEYS.all, 'detail', id, languageCode ?? 'default'] as const,
}

interface TenantScopedMutationVariables {
  tenantId?: string
}

function buildTenantScopedOptions(tenantId?: string) {
  return tenantId ? { tenantId } : {}
}

export function useTenantDestinationsQuery(
  params: TenantDestinationListParams,
  options?: { enabled?: boolean; languageCode?: string },
) {
  return useQuery({
    queryKey: TENANT_DESTINATION_QUERY_KEYS.list(params, options?.languageCode),
    queryFn: async () =>
      listTenantDestinations(
        params,
        options?.languageCode ? { languageCode: options.languageCode } : {},
      ),
    enabled: options?.enabled ?? true,
  })
}

export function useTenantDestinationByIdQuery(
  id: string,
  languageCode?: string,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: TENANT_DESTINATION_QUERY_KEYS.detail(id, languageCode),
    queryFn: async () => getTenantDestinationById(id, languageCode),
    enabled: !!id && (options?.enabled ?? true),
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

async function invalidateDependentQueries(queryClient: ReturnType<typeof useQueryClient>) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: POI_QUERY_KEYS.all }),
    queryClient.invalidateQueries({ queryKey: TOUR_QUERY_KEYS.all }),
    queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
  ])
}

export function useCreateTenantDestinationMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      tenantId,
      payload,
    }: { payload: CreateTenantDestinationPayload } & TenantScopedMutationVariables) =>
      createTenantDestination(payload, buildTenantScopedOptions(tenantId)),
    onSuccess: async (response) => {
      await queryClient.invalidateQueries({ queryKey: TENANT_DESTINATION_QUERY_KEYS.listPrefix() })
      await queryClient.invalidateQueries({
        queryKey: TENANT_DESTINATION_QUERY_KEYS.detailPrefix(response.data.id),
      })
      await invalidateDependentQueries(queryClient)
    },
  })
}

export function useUpdateTenantDestinationMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      payload,
      tenantId,
    }: { id: string; payload: UpdateTenantDestinationPayload } & TenantScopedMutationVariables) =>
      updateTenantDestination(id, payload, buildTenantScopedOptions(tenantId)),
    onSuccess: async (_response, { id }) => {
      await queryClient.invalidateQueries({ queryKey: TENANT_DESTINATION_QUERY_KEYS.listPrefix() })
      await queryClient.invalidateQueries({
        queryKey: TENANT_DESTINATION_QUERY_KEYS.detailPrefix(id),
      })
      await invalidateDependentQueries(queryClient)
    },
  })
}

export function useDeleteTenantDestinationMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, tenantId }: { id: string } & TenantScopedMutationVariables) =>
      deleteTenantDestination(id, buildTenantScopedOptions(tenantId)),
    onSuccess: async (_response, { id }) => {
      await queryClient.invalidateQueries({ queryKey: TENANT_DESTINATION_QUERY_KEYS.listPrefix() })
      queryClient.removeQueries({ queryKey: TENANT_DESTINATION_QUERY_KEYS.detailPrefix(id) })
      await invalidateDependentQueries(queryClient)
    },
  })
}
